
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Client } from "./types";
import { EnrichmentResponse } from '@/hooks/useCompanyEnrichment';
import { useEnrichmentDataProcessor } from '@/hooks/useEnrichmentDataProcessor';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { mappableFields as globalMappableFields } from './enrichment-utils';
import { EnrichmentDialogContentSwitcher } from './enrichment-dialog/EnrichmentDialogContentSwitcher';
import { EnrichmentDialogFooter } from './enrichment-dialog/EnrichmentDialogFooter';
import { isSafeForUpdates, assertEnrichedData } from '@/utils/typeGuards';

interface ClientEnrichmentResultsDialogProps {
  client: Client;
  enrichmentResult: EnrichmentResponse | null;
  isOpen: boolean;
  onClose: () => void;
  onApplied: () => void;
  onCancelEnrichment: (runId: string, clientId: string) => Promise<void>;
  isCancellingEnrichment: boolean;
}

function isErrorWithMessage(error: unknown): error is { message: string } {
  return typeof error === 'object' && error !== null && 'message' in error && typeof (error as { message: unknown }).message === 'string';
}

export const ClientEnrichmentResultsDialog: React.FC<ClientEnrichmentResultsDialogProps> = ({
  client,
  enrichmentResult,
  isOpen,
  onClose,
  onApplied,
  onCancelEnrichment,
  isCancellingEnrichment,
}) => {
  const {
    dialogState,
    actualCompanyData,
    selectedFields,
    handleToggleField,
    derivedMessage,
    derivedLutraRunId,
    errorDetails,
    rawDebugResponse,
    initialLutraResponse,
    backendProgressStage,
    estimatedCompletionAt,
  } = useEnrichmentDataProcessor({
    client,
    enrichmentResult: enrichmentResult as Partial<EnrichmentResponse> | null,
    mappableFields: globalMappableFields,
  });

  const [isApplyingChanges, setIsApplyingChanges] = useState(false);

  const handleApplyChanges = async () => {
    if (!actualCompanyData) {
      toast.error("No enriched data available to apply.");
      return;
    }
    setIsApplyingChanges(true);

    const updates: Partial<Client> = {};
    let changesMade = false;

    globalMappableFields.forEach(field => {
      if (selectedFields[field.key]) {
        const enrichedKeySpec = globalMappableFields.find(f => f.key === field.key)?.enrichedKey;
        if (!enrichedKeySpec) return;

        let valueToApply: unknown;
        const enrichedVal = (() => {
            if (Array.isArray(enrichedKeySpec)) {
                for (const k of enrichedKeySpec) if (actualCompanyData[k] !== undefined) return actualCompanyData[k];
                return undefined;
            }
            return actualCompanyData[enrichedKeySpec];
        })();

        if (field.transform && enrichedVal !== undefined) {
          valueToApply = field.transform(enrichedVal);
        } else {
          valueToApply = enrichedVal;
        }
        
        if (valueToApply !== undefined && isSafeForUpdates(valueToApply)) {
          (updates as Record<string, unknown>)[field.key] = valueToApply;
          changesMade = true;
        }
      }
    });

    if (!changesMade) {
      toast.info("No changes selected to apply.");
      setIsApplyingChanges(false);
      onClose(); 
      return;
    }
    
    updates.enrichment_status = 'none'; 

    try {
      toast.info(`Applying updates to ${client.name}...`);
      const { error } = await supabase
        .from('clients')
        .update(updates)
        .eq('id', client.id);

      if (error) {
        throw error;
      }

      if (derivedLutraRunId) { 
        await supabase
          .from('client_enrichment_results')
          .update({ 
            status: 'applied', 
            progress_stage: 'applied', 
          })
          .eq('run_id', derivedLutraRunId)
          .eq('client_id', client.id); 
      }

      onApplied(); 
    } catch (error: unknown) {
      console.error("Error applying enriched data:", error);
      const errorMessage = isErrorWithMessage(error) ? error.message : 'Unknown error occurred';
      toast.error(`Failed to apply changes: ${errorMessage}`);
    } finally {
      setIsApplyingChanges(false);
    }
  };

  const canApplyChanges = !!actualCompanyData && Object.values(selectedFields).some(v => v);
  
  const runIdToCancel = derivedLutraRunId || enrichmentResult?.lutra_run_id || client.last_enrichment_run_id;

  // Fix the errorDetails type assertion
  const processedErrorDetails = errorDetails ? {
    error: errorDetails.error,
    details: typeof errorDetails.details === 'string' || 
             (typeof errorDetails.details === 'object' && errorDetails.details !== null) 
             ? errorDetails.details as string | Record<string, unknown>
             : undefined
  } : null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl md:max-w-3xl lg:max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Company Enrichment for {client.name}</DialogTitle>
        </DialogHeader>
        <div className="flex-grow overflow-y-auto p-1 pr-2">
          <EnrichmentDialogContentSwitcher
            dialogState={dialogState}
            client={client}
            derivedMessage={derivedMessage}
            backendProgressStage={backendProgressStage}
            derivedLutraRunId={derivedLutraRunId}
            initialLutraResponse={assertEnrichedData(initialLutraResponse, 'initialLutraResponse')}
            rawDebugResponse={assertEnrichedData(rawDebugResponse, 'rawDebugResponse')}
            onClose={onClose}
            errorDetails={processedErrorDetails}
            actualCompanyData={actualCompanyData}
            selectedFields={selectedFields}
            handleToggleField={handleToggleField}
            estimatedCompletionAt={estimatedCompletionAt}
          />
        </div>
        <EnrichmentDialogFooter
          dialogState={dialogState}
          onClose={onClose}
          onApplyChanges={handleApplyChanges}
          isApplyingChanges={isApplyingChanges}
          canApplyChanges={canApplyChanges}
          onCancelEnrichment={onCancelEnrichment}
          isCancellingEnrichment={isCancellingEnrichment}
          currentRunId={runIdToCancel}
          clientId={client.id}
        />
      </DialogContent>
    </Dialog>
  );
};
